import { useTranslation } from 'react-i18next';
import { useState, type FormEvent } from 'react';
import type { PlaceDetail } from '../../api/endpoints/places';
import { adminAddTrackingEvent } from '../../api/endpoints/admin';
import { AddressAutocompleteField } from '../../components/domain/AddressAutocompleteField';
import { useMutationHook } from '../../hooks/useMutationHook';
import styles from '../../components/domain/FormModal.module.css';
import { DialogShell } from '../../components/ui/DialogShell';
import { useDialogDismiss, useIsDirty } from '../../components/ui/dialogDismiss';
import { DialogTitleRow } from '../../help/DialogTitleRow';

interface Props {
  shipmentId: number;
  onClose: () => void;
}

export function InTransitLocationModal({ shipmentId, onClose }: Props) {
  const { t } = useTranslation();
  const [addressText, setAddressText] = useState('');
  const [selectedPlace, setSelectedPlace] = useState<PlaceDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutationHook(
    () =>
      adminAddTrackingEvent(shipmentId, {
        type: 'LOCATION_PING',
        latitude: selectedPlace?.lat ?? undefined,
        longitude: selectedPlace?.lng ?? undefined,
        locationName: selectedPlace?.formattedAddress || addressText.trim() || undefined,
      }),
    {
      successMessage: 'In-transit location checkpoint logged.',
      invalidateKeys: [
        ['admin', 'shipments', 'tracking', shipmentId],
        ['admin', 'shipments', 'detail', shipmentId],
        ['admin', 'shipments'],
      ],
      onSuccess: onClose,
    },
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!addressText.trim() && !selectedPlace) {
      return setError('Please search and select a location or checkpoint.');
    }
    setError(null);
    saveMut.mutate(undefined);
  };

  const dirty = useIsDirty({ addressText });
  const dismiss = useDialogDismiss({ dirty, busy: saveMut.isLoading, onClose });

  return (
    <DialogShell dismiss={dismiss} label={t('shipments.inTransitLocationModal.dialogLabel', { shipmentId })} scrimClassName={styles.scrim} panelClassName={styles.panel}>
      <DialogTitleRow context="trip-status" title={t('shipments.inTransitLocationModal.title', { shipmentId })}>
        <h3 className={styles.title}>{t('shipments.inTransitLocationModal.title', { shipmentId })}</h3>
      </DialogTitleRow>
      <p className={styles.hint}>
        {t('shipments.inTransitLocationModal.hint')}
      </p>

      <form onSubmit={handleSubmit}>
        <div style={{ marginTop: 14 }}>
          <AddressAutocompleteField
            label={t('shipments.inTransitLocationModal.locationLabel')}
            placeholder={t('shipments.inTransitLocationModal.locationPlaceholder')}
            value={addressText}
            onChange={setAddressText}
            onPlaceSelected={(detail) => setSelectedPlace(detail)}
            onCleared={() => setSelectedPlace(null)}
            required
          />
          {selectedPlace && (
            <div
              style={{
                marginTop: 6,
                padding: '6px 10px',
                background: 'var(--status-green-bg)',
                border: '1px solid var(--accent)',
                borderRadius: 'var(--radius-button)',
                fontSize: 11.5,
                color: 'var(--ink)',
              }}
            >
              {t('shipments.inTransitLocationModal.geocoded')}<strong>{selectedPlace.city || selectedPlace.formattedAddress}</strong>{' '}
              <span style={{ color: 'var(--muted)' }}>
                {t('shipments.inTransitLocationModal.coordinates', {
                  lat: selectedPlace.lat?.toFixed(4) ?? '',
                  lng: selectedPlace.lng?.toFixed(4) ?? '',
                })}
              </span>
            </div>
          )}
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={dismiss.requestClose} disabled={saveMut.isLoading}>
            {t('common.actions.cancel')}
          </button>
          <button type="submit" className={styles.submit} disabled={saveMut.isLoading}>
            {saveMut.isLoading ? 'Logging Location…' : 'Log Location Ping'}
          </button>
        </div>
      </form>
    </DialogShell>
  );
}
